import { format } from 'date-fns';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrderConfirmationDrawer from '../../components/OrderConfirmationDrawer/OrderConfirmationDrawer';
import { BillStatus, OrderItemStatus, TABS } from '../../consts';
import { useNewOrderContext } from '../../context/NewOrderContext';
import { useSystemContext } from '../../context/SystemContext/SystemContext';
import backgroundImg from '../../stadium.png';
import ConfirmModal from './components/ConfirmModal';
import ErrorModal from './components/ErrorModal';
import RegularUnavailableModal from './components/RegularUnavailableModal';
import SeatInfoForm, { SeatInfoFormData } from './SeatInfoForm';
import { getJstDateKey, isSameJstDate, parseJstDateTime } from '../../utils/jstDate';

const SeatInfoTab = ({ setActiveTab }: { setActiveTab: (tab: TABS, force?: boolean) => void }) => {
  const { t } = useTranslation();
  const orderId = sessionStorage.getItem('orderId') || '';
  const preOrderCheck = useRef<boolean>(false);

  const { seatInfo, updateSeatInfo, confirmSeatInfo, isLoadingSeat, isPreOrderWindowForDate, getGameDayPreorder, termsAndConditionsData } =
    useSystemContext();
  const { orderDetails, updateOrderDetails, updateOrderId } = useNewOrderContext();

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRegularUnavailableModalOpen, setIsRegularUnavailableModalOpen] = useState(false);
  const [unavailableDeliveryTime, setUnavailableDeliveryTime] = useState('');
  const [isDeliveryTimeModalOpen, setIsDeliveryTimeModalOpen] = useState(false);

  const headerHeight = !!seatInfo?.id && seatInfo?.isConfirmed ? '140px' : '70px';

  const getOrderDateKey = (orderDate?: string) => {
    if (!orderDate) return '';
    try {
      return getJstDateKey(orderDate);
    } catch {
      return orderDate;
    }
  };

  const hasCartBreakingSeatInfoChanged = (data: SeatInfoFormData) => {
    if (!seatInfo?.id) return true;

    return (
      getOrderDateKey(seatInfo.order_date) !== getOrderDateKey(data.order_date) ||
      seatInfo.seat_type !== data.seat_type ||
      seatInfo.area !== data.area ||
      seatInfo.aisle !== data.aisle ||
      seatInfo.block !== data.block ||
      seatInfo.row !== data.row ||
      seatInfo.seat_number !== data.seat_number
    );
  };

  const onSubmit = async (data: SeatInfoFormData) => {
    if (!isConfirmed) {
      setConfirmError(true);
      return;
    }

    try {
      const today = isSameJstDate(data.order_date);
      preOrderCheck.current = getGameDayPreorder(data.order_date) && isPreOrderWindowForDate(data.order_date);
      const shouldResetOrder = hasCartBreakingSeatInfoChanged(data);

      const result = await updateSeatInfo(data, preOrderCheck.current);
      if (shouldResetOrder) {
        updateOrderDetails({
          bill_status: BillStatus.NOT_CLEARED,
          foods: [],
          promotion_foods: [],
          order_status: OrderItemStatus.PREPARING,
          order_table_number: '',
          seat_type: data.seat_type,
          seat_number: data.seat_number,
          payment_method: '',
          stadium_id: '',
          delivery_request: '',
        });
      }

      // Today + seat doesn't support pre-order: show RegularUnavailableModal only when we're still
      // before game/delivery time. After delivery time → allow through to confirm and show regular menu.
      if (today && result.pre_order === false && isPreOrderWindowForDate(data.order_date)) {
        const deliveryTime = format(parseJstDateTime(data.order_date), 'HH:mm');
        setUnavailableDeliveryTime(deliveryTime);
        setIsRegularUnavailableModalOpen(true);
        return;
      }

      setIsConfirmModalOpen(true);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'ERROR_SELECTING_SEAT';
      setErrorMessage(errorMsg);
      setIsErrorModalOpen(true);
    }
  };

  const handleConfirm = async () => {
    if (seatInfo) {
      await confirmSeatInfo();

      updateOrderId(seatInfo.id);

      if (preOrderCheck.current) {
        handleCloseModal();
        setIsDeliveryTimeModalOpen(true);
        return;
      }

      setActiveTab(TABS.HOME, true);
      handleCloseModal();
    }
  };

  const handleDeliveryTimeConfirm = ({ deliveryRequest }: { deliveryRequest: string }) => {
    const shouldClearCart = preOrderCheck.current && orderDetails.delivery_request && orderDetails.delivery_request !== deliveryRequest;
    updateOrderDetails({
      ...orderDetails,
      delivery_request: deliveryRequest,
      foods: shouldClearCart ? [] : orderDetails.foods,
      promotion_foods: shouldClearCart ? [] : orderDetails.promotion_foods,
    });
    setIsDeliveryTimeModalOpen(false);
    setActiveTab(TABS.HOME, true);
  };

  const handleCloseModal = () => {
    setIsConfirmModalOpen(false);
  };

  const handleCloseErrorModal = () => {
    setIsErrorModalOpen(false);
    setErrorMessage('');
  };

  const handleCloseRegularUnavailableModal = () => {
    setIsRegularUnavailableModalOpen(false);
    setUnavailableDeliveryTime('');
  };

  const onConfirmSaveDataChange = (isChecked: boolean) => {
    setIsConfirmed(isChecked);
    if (isChecked) {
      setConfirmError(false);
    }
  };

  return (
    <>
      <div className="relative h-full bg-gray-50 flex flex-col">
        {/* Header with image */}
        <div className="relative flex flex-col">
          <div
            className="absolute top-0 left-0 w-full z-[1]"
            style={{
              height: headerHeight,
              background: 'linear-gradient(180deg, #30893C 0%, rgba(52, 143, 64, 0.72) 69.6%, rgba(61, 159, 74, 0.00) 100%)',
            }}
          ></div>
          <div className="bg-no-repeat bg-cover" style={{ height: headerHeight, backgroundImage: `url(${backgroundImg})` }}>
            {!!seatInfo?.id && seatInfo?.isConfirmed && (
              <div className="flex items-start px-4 pt-6 justify-between">
                <div className="flex flex-col gap-1 z-[2]">
                  <p className="text-white opacity-70 text-sm leading-5 not-italic font-medium">{t('HOME_SEAT_TITLE')}</p>
                  <p className="text-white text-sm not-italic leading-5 font-semibold">{seatInfo?.seat_type}</p>
                  <p className="text-white text-sm not-italic leading-5 font-semibold">{seatInfo?.seat_number}</p>
                </div>
                <div className="flex flex-col gap-1 z-[2]">
                  <p className="text-white opacity-70 text-sm leading-5 not-italic font-medium text-right">{t('HOME_ORDERID_TITLE')}</p>
                  <p className="text-white text-sm not-italic leading-5 font-semibold">{orderId}</p>
                </div>
              </div>
            )}
            {!(!!seatInfo?.id && seatInfo?.isConfirmed) && (
              <div className="w-full h-full flex items-center justify-between">
                <div className="w-full h-full flex items-center justify-center z-[2]">
                  <p className="text-white text-center text-sm font-bold">{t('MEJI_DESCRIPTION')}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Container */}
        <div className="p-4 flex flex-col gap-4 flex-1">
          <p className="text-sm text-gray-600 mb-5">{t('SEAT_INFO_HEADER_TEXT')}</p>

          <SeatInfoForm editable={true} onSubmit={onSubmit} isConfirmed={isConfirmed} confirmError={confirmError} onConfirmChange={onConfirmSaveDataChange} />

          {/* Terms and Conditions */}
          <div className="pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('SEAT_INFO_TNC')}</h3>
            {termsAndConditionsData && (
              <div className="text-sm text-gray-600 space-y-2" dangerouslySetInnerHTML={{ __html: termsAndConditionsData }} />
            )}
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      {seatInfo && (
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirm}
          data={seatInfo}
          isLoading={isLoadingSeat}
          isPreOrder={preOrderCheck.current}
        />
      )}

      {/* Error Modal — for general errors (e.g. THIS_SEAT_CAN_NOT_PRE_ORDER) */}
      <ErrorModal isOpen={isErrorModalOpen} onClose={handleCloseErrorModal} errorMessage={errorMessage} />

      {/* Regular Unavailable Modal — for same-day + before game time + seat no pre-order */}
      <RegularUnavailableModal isOpen={isRegularUnavailableModalOpen} onClose={handleCloseRegularUnavailableModal} time={unavailableDeliveryTime} />

      <OrderConfirmationDrawer
        open={isDeliveryTimeModalOpen}
        onClose={() => setIsDeliveryTimeModalOpen(false)}
        onConfirm={handleDeliveryTimeConfirm}
        preOrderSetup
      />
    </>
  );
};

export default SeatInfoTab;
